/* eslint-disable @typescript-eslint/no-misused-promises */
import {
  useContext, useEffect, useRef, useState,
} from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import apiSettingsData from 'assets/api-settings.json';

import { StoreContext, ImportSectionEnum } from 'models/import';
import ContentBlock, { ContentBlockModifier } from 'components/contentBlock';
import { CtPlatformOption, CtPlatformItem, CtPlatformImportTypeEnum } from 'models/general';
import FormTextfield from 'components/form/textfield';
import FormCheckbox from 'components/form/checkbox';
import Dropdown from 'components/dropdown';
import ImportDropZone from 'pages/import/platform/dropzone';
import Actions, { ActionModifier } from 'components/actions';
import Btn from 'components/btn';
import { useNavigate } from 'react-router-dom';
import { getLaminasApiData, postMockData } from 'services/api';

export type ImportPlatformAllowedSection =
  'credential_type' |
  'autosync' |
  'options' |
  'timerange' |
  'name_mapping' |
  'notifications';

export interface ImportPlatformFormField {
  type: 'text' | 'checkbox' | 'dropzone';
  name: string;
  label?: string;
  placeholder?: string;
  minLength?: number;
  maxLength?: number;
  required?: boolean;
  disabled?: boolean;

  // checkbox
  desc?: string;
  narrow?: boolean;
  toggle?: boolean;
  defaultChecked?: boolean;
}

export interface ImportPlatformSettingsSection {
  title: string;
  desc: string;
  isMain: boolean;
  as: ImportPlatformAllowedSection;
  fields: ImportPlatformFormField[];
}

interface ImportPlatformSettings {
  main: ImportPlatformSettingsSection[];
  advanced: ImportPlatformSettingsSection[];
}

export interface ImportPlatformSettingsJson {
  sections: ImportPlatformSettingsSection[];
}

function ImportPlatformFormComponent() {
  const [settings, setSettings] = useState({} as ImportPlatformSettings);
  const [updateHeight, setUpdateHeight] = useState({ updateHeight: false });
  const [isLoading, setIsLoading] = useState(true);
  const ref = useRef<HTMLFormElement>(null);

  const {
    baseItems, importName, setActiveSection, setStep, importType,
  } = useContext(StoreContext);

  const getPlatformItem = (_name: string) => getLaminasApiData<CtPlatformItem>(`/import/detail?import-name=${_name}`);

  const formMethods = useForm({ mode: 'onChange' });

  const submitData = (data: unknown) => postMockData<unknown, unknown>('/import_submit/', data);

  const onSubmit = (data: unknown) => {
    const selectedImportType = (importType && importType.data) || '';
    // eslint-disable-next-line no-console
    console.log('form submit', data);

    if (data) {
      submitData(data).then((res) => {
        // eslint-disable-next-line no-console
        console.log(res);

        let successType;

        if (selectedImportType === CtPlatformImportTypeEnum.API) {
          successType = ImportSectionEnum.SUCCESS_API;
        } else if (selectedImportType === CtPlatformImportTypeEnum.CSV) {
          successType = ImportSectionEnum.SUCCESS_CSV;
        } else {
          return;
        }

        // TODO transfer Data (id or data response for apiCard) to platform

        setActiveSection({ data: successType, ref: undefined });
        setStep(2);
      }, () => { });
    }
  };

  // TODO: if api card is selected, import apiSettingsData
  const { sections } = apiSettingsData as ImportPlatformSettingsJson;

  const extractTruthyKeys = (obj: Record<string, unknown>): string[] => Object.keys(obj)
    .filter((k) => Boolean(obj[k]));

  const mapping = {
    credential_type: (str: string) => str.split('-'),
    name_mapping: (obj: Record<string, unknown>) => extractTruthyKeys(obj),
    timerange: (obj: Record<string, unknown>) => extractTruthyKeys(obj),
    options: (arr: CtPlatformOption[]) => arr.map((o) => ({
      type: 'checkbox',
      name: o.name || '',
      label: o.display_name || '',
      desc: o.description || '',
      defaultChecked: Boolean(o.checked_by_default),
      disabled: Boolean(o.disabled),
      required: Boolean(o.required),
    })),
  };

  useEffect(() => {
    formMethods.reset();

    const selectedImportType = (importName && importName.data) || '';

    if (selectedImportType && baseItems && baseItems.length) {
      getPlatformItem(selectedImportType)
        .then((res) => {
          const importSettings = res.data;

          // update content height of dropdown in case content has changed
          setUpdateHeight({ ...updateHeight, updateHeight: true });
          setIsLoading(false);

          if (importSettings && Object.keys(importSettings).length) {
            const allowedSections = sections
              .filter(
                (s) => Object.keys(importSettings).includes(s.as) && Boolean(importSettings[s.as]),
              )
              .map((s) => {
                switch (s.as) {
                  case 'options':
                    return {
                      ...s,
                      fields: mapping[s.as](importSettings[s.as] || []),
                    } as ImportPlatformSettingsSection;

                  default:
                    return {
                      ...s,
                      fields: s.fields.filter((f) => {
                        switch (s.as) {
                          case 'credential_type':
                            return (mapping[s.as](importSettings[s.as] || '')).includes(f.name);
                          case 'name_mapping':
                          case 'timerange':
                            return (mapping[s.as](importSettings[s.as] || {})).includes(f.name);

                          default:
                            return f;
                        }
                      }),
                    } as ImportPlatformSettingsSection;
                }
              })
              .filter((s) => Boolean(s.fields.length));

            setActiveSection({
              data: ImportSectionEnum.FORM,
              ref: ref && ref.current,
              instructions: importSettings.instructions,
            });

            setSettings({
              main: allowedSections.filter((s) => s.isMain),
              advanced: allowedSections.filter((s) => !s.isMain),
            });
          }
        }, () => { });
    }
  }, [importName]);

  const navigate = useNavigate();

  return (
    <FormProvider {...formMethods}>
      <form className="ct_form" ref={ref} onSubmit={formMethods.handleSubmit(onSubmit)}>
        {((settings && settings.main) || []).map((s, index) => (
          <ContentBlock
            key={index}
            title={s.title}
            desc={s.desc}
            modifiers={[ContentBlockModifier.MAIN]}
          >
            <>
              {((s.fields) || []).map((f, j) => {
                const field = { ...f, name: f.name, options: { ...f } };

                if (f.type === 'text') {
                  return <FormTextfield key={j} {...field} />;
                }
                if (f.type === 'checkbox') {
                  return <FormCheckbox key={j} {...field} />;
                }
                if (f.type === 'dropzone') {
                  return <ImportDropZone key={j} />;
                }
                return null;
              })}
            </>
          </ContentBlock>
        ))}
        {settings && settings.advanced && (
          <Dropdown title="Advanced settings" updateHeight={updateHeight}>
            <>
              {settings.advanced.map((s, index) => (
                <ContentBlock
                  key={index}
                  title={s.title}
                  desc={s.desc}
                  modifiers={[ContentBlockModifier.SUB]}
                >
                  <>
                    {((s.fields) || []).map((f, j) => {
                      const field = { ...f, name: f.name, options: { ...f } };

                      if (f.type === 'text') {
                        return <FormTextfield key={j} {...field} />;
                      }
                      if (f.type === 'checkbox') {
                        return <FormCheckbox key={j} {...field} />;
                      }
                      return null;
                    })}
                  </>
                </ContentBlock>
              ))}
            </>
          </Dropdown>
        )}
        {!isLoading && (
          <Actions modifiers={[ActionModifier.RIGHT, ActionModifier.NARROW]}>
            <Btn onClick={() => navigate(-1)} type="secondary" text="Back" />
            <Btn
              btnType="submit"
              type="primary"
              text="Start Import"
              disabled={!formMethods.formState.isValid}
            />
          </Actions>
        )}
      </form>
    </FormProvider>
  );
}

export default ImportPlatformFormComponent;
